// The snapping context of one gesture: candidates from the current display list (minus what is being moved),
// the artboards and the guides, the user's snap settings, and the tolerance (6 screen px). Tools call
// `snapContext(exclude)` on pointer-down and then snap every move against it; the guides it returns are
// drawn by `drawSnapGuides`.
import { currentList } from '../render/instance';
import { useDesign } from '../state';
import { useDesignUi } from '../ui/designUi';
import { buildSource, snapBox, snapPoint, snapAngle, type Guide, type SnapSource } from '../geometry/snapping';
import { worldSubpaths } from './pathTarget';
import { toScreen } from '../render/viewMath';
import type { View } from '../state';
import type { Rect } from '../model/types';

export const SNAP_PX = 6;
const MAX_PATHS = 600; // path outlines considered (those in view): enough for drawing, cheap per move

export type SnapCtx = {
  src: SnapSource;
  tol: number;
  box: (box: Rect, dx: number, dy: number) => { dx: number; dy: number; guides: Guide[] };
  point: (x: number, y: number) => { x: number; y: number; guides: Guide[] };
  angle: (x0: number, y0: number, x: number, y: number) => { x: number; y: number; guides: Guide[] };
};

export function snapContext(excludeTopIds: Iterable<string> = [], excludeIds: Iterable<string> = []): SnapCtx {
  const list = currentList();
  const { doc, view } = useDesign.getState();
  const o = useDesignUi.getState().snap;
  const exTop = new Set(excludeTopIds),
    exId = new Set(excludeIds);
  const tol = SNAP_PX / view.zoom;
  // Objects in view (plus a margin) give points and path outlines; every object gives box edges.
  const vx0 = -view.panX / view.zoom - 200 / view.zoom,
    vy0 = -view.panY / view.zoom - 200 / view.zoom;
  const inView = new Set(list.tree.search({ minX: vx0, minY: vy0, maxX: vx0 + 4000 / view.zoom, maxY: vy0 + 3000 / view.zoom }));
  let pathsLeft = MAX_PATHS;
  const objects = list.items
    .filter((it) => !it.hidden && !exTop.has(it.topId) && !exId.has(it.id))
    .map((it) => ({
      gbounds: it.gbounds,
      subpaths: inView.has(it) && pathsLeft-- > 0 && (it.node.kind === 'path' || it.node.kind === 'shape') ? worldSubpaths(it) : undefined,
    }));
  const src = buildSource(objects, doc?.artboards ?? [], doc?.guides ?? [], { ...o, pixelSize: 72 / (doc?.rasterDpi ?? 72) });
  const opts = { ...o, pixelSize: 72 / (doc?.rasterDpi ?? 72) };
  const off = !o.enabled;
  return {
    src,
    tol,
    box: (b, dx, dy) => (off ? { dx, dy, guides: [] } : snapBox(b, dx, dy, src, opts, tol)),
    point: (x, y) => (off ? { x, y, guides: [] } : snapPoint(x, y, src, opts, tol)),
    angle: (x0, y0, x, y) => (off ? { x, y, guides: [] } : snapAngle(x0, y0, x, y, opts)),
  };
}

/** Smart guides on the overlay: magenta alignment lines, spacing chips, angle rays, snapped points. */
export function drawSnapGuides(g: CanvasRenderingContext2D, v: View, guides: Guide[]) {
  g.save();
  g.strokeStyle = '#ff2fd0';
  g.fillStyle = '#ff2fd0';
  g.lineWidth = 1;
  g.font = '10px system-ui, sans-serif';
  for (const gd of guides) {
    if (gd.kind === 'line') {
      const [ax, ay] = gd.axis === 'x' ? toScreen(v, gd.v, gd.from) : toScreen(v, gd.from, gd.v);
      const [bx, by] = gd.axis === 'x' ? toScreen(v, gd.v, gd.to) : toScreen(v, gd.to, gd.v);
      g.beginPath();
      g.moveTo(Math.round(ax) + 0.5, Math.round(ay) + 0.5);
      g.lineTo(Math.round(bx) + 0.5, Math.round(by) + 0.5);
      g.stroke();
    } else if (gd.kind === 'spacing') {
      for (const [a, b] of gd.gaps) {
        const [x0, y0] = gd.axis === 'x' ? toScreen(v, a, gd.at) : toScreen(v, gd.at, a);
        const [x1, y1] = gd.axis === 'x' ? toScreen(v, b, gd.at) : toScreen(v, gd.at, b);
        g.beginPath();
        g.moveTo(x0, y0);
        g.lineTo(x1, y1);
        g.stroke();
        const label = `${+(b - a).toFixed(2)}`;
        g.fillText(label, (x0 + x1) / 2 - g.measureText(label).width / 2, (y0 + y1) / 2 - 4);
      }
    } else if (gd.kind === 'angle') {
      const [x, y] = toScreen(v, gd.x, gd.y);
      const a = (-gd.deg * Math.PI) / 180;
      g.beginPath();
      g.moveTo(x - Math.cos(a) * 2000, y - Math.sin(a) * 2000);
      g.lineTo(x + Math.cos(a) * 2000, y + Math.sin(a) * 2000);
      g.setLineDash([4, 4]);
      g.stroke();
      g.setLineDash([]);
      g.fillText(`${gd.deg}°`, x + 8, y - 8);
    } else {
      const [x, y] = toScreen(v, gd.x, gd.y);
      g.beginPath();
      g.arc(x, y, 4, 0, Math.PI * 2);
      g.stroke();
    }
  }
  g.restore();
}
